import { z } from 'zod'

import { roleSchema, type TeamUserRole } from '@/team'
import { emailSchema, nanoidSchema } from '@/utility'

// Access token payload will move to being user defined in the future
// Must always be a flat primitive record like Record<string, string|number|boolean>
export type AccessTokenPayload = {
  userIndex: string
  userUid: string
  teamUid: string
  email: string | null
  role?: TeamUserRole | undefined
  exp: number
}

export const accessTokenPayloadSchema = z
  .object({
    userIndex: z.string(),
    userUid: z.string(),
    teamUid: z.string(),
    email: emailSchema.nullable(),
    role: roleSchema.optional(),
    exp: z.number().int(),
  })
  .meta({ id: 'access-token-payload' }) satisfies z.ZodType<AccessTokenPayload>

export type RefreshTokenPayload = {
  /** Uid of token */
  uid: string
  /** Uid of team */
  teamUid: string | null
  /** Type of the token. Always set to 'refresh' for now */
  type: string
  /** Token family ID to associate a login sessions set of refresh tokens */
  familyId: string
  /** Unique user index to query user record */
  userIndex: string
  /** Expiry in seconds */
  exp: number
}
export const refreshTokenPayloadSchema: z.ZodType<RefreshTokenPayload> = z
  .object({
    uid: z.string(),
    teamUid: z.string().nullable(),
    type: z.string(),
    familyId: z.string(),
    userIndex: z.string(),
    exp: z.number(),
  })
  .meta({ id: 'refresh-token-payload' })

export type PersonalTokenPayload = {
  /** Uid of token */
  uid: string
  /** Uid of team */
  teamUid: string
  /** Unique user index to query user record */
  userIndex: string
  /** Token generation timestamp in unix seconds */
  iat: number
  /** Expiry in seconds */
  exp: number
}

/**
 * Long lived personal token related to a user and a team.
 */
export const personalTokenPayloadSchema: z.ZodType<PersonalTokenPayload> = z
  .object({
    uid: z.string(),
    teamUid: z.string(),
    userIndex: z.string(),
    iat: z.number(),
    exp: z.number(),
  })
  .meta({ id: 'personal-token-payload' })

/**
 * Short lived exchange token to retrieve recently issued
 * access and refresh tokens from the database.
 */
export const exchangeTokenSchema = z
  .object({
    uid: z.string(),
    accessToken: z.string(),
    refreshToken: z.string(),
    expiry: z.number(),
    teamUid: z.string(),
  })
  .meta({ id: 'exchange-token' })

export type ExchangeTokenPayload = z.infer<typeof exchangeTokenSchema>

export const hostingExchangeTokenSchema = z
  .object({
    email: emailSchema,
    aud: z.url(),
    idp: nanoidSchema,
    teamUid: nanoidSchema,
    exp: z.number(),
  })
  .meta({ id: 'hosting-exchange-token' })

export type HostingExchangeTokenPayload = z.infer<
  typeof hostingExchangeTokenSchema
>
